import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import Annotation from "~/components/build/Show/components/Annotation";
import {
  FilteredAnnotationsEmptyState,
  NoAnnotationsEmptyState,
} from "~/components/build/Show/components/AnnotationsEmptyState";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { allAnnotationsQueryOptions } from "~/components/build/Show/lib/queries/allAnnotations";
import {
  type Annotation as AnnotationType,
  type AnnotationScope,
} from "~/components/build/Show/lib/types";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import { preserveNonDrawerParams } from "~/components/build/Show/lib/urlForView";
import Spinner from "~/components/shared/Spinner";

function scopesToScope(scopes: AnnotationScope[]): AnnotationScope | null {
  // Empty selection or both scopes selected → no scope filter (return both from the API).
  if (scopes.length === 0 || scopes.length === 2) {
    return null;
  }
  return scopes[0];
}

function useJobUrl(jobUuid: string) {
  const { search } = useLocation();
  return preserveNonDrawerParams("", search, { jid: jobUuid, tab: "output" });
}

function AnnotationItem({ annotation }: { annotation: AnnotationType }) {
  if (annotation.scope === "job" && annotation.jobUuid) {
    return <JobAnnotationItem annotation={annotation} />;
  }
  return <Annotation annotation={annotation} />;
}

function JobAnnotationItem({ annotation }: { annotation: AnnotationType }) {
  const jobUrl = useJobUrl(annotation.jobUuid ?? "");
  return (
    <div>
      <div className="flex justify-end">
        <Link to={jobUrl} className="text-charcoal-300 hover:text-charcoal-700 mb-1 text-xs">
          View job →
        </Link>
      </div>
      <Annotation annotation={annotation} />
    </div>
  );
}

type Props = {
  scopes: AnnotationScope[];
  totalAnnotationsCount: number;
};

export default function AnnotationsList({ scopes, totalAnnotationsCount }: Props) {
  const { build } = useBuild();
  const navigateClickHandler = useReactRouterNavigateHandler();
  const scope = scopesToScope(scopes);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    allAnnotationsQueryOptions(build, scope, { placeholderData: keepPreviousData }),
  );

  const handleShowMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const annotations = useMemo(() => {
    return (data?.pages || []).flatMap((page) => page.records);
  }, [data?.pages]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center space-x-2">
        <Spinner className="h-4 w-4" />
        <span>Fetching annotations...</span>
      </div>
    );
  }

  if (annotations.length === 0) {
    // Differentiate between "build genuinely has no annotations" and "filter hides them all".
    if (totalAnnotationsCount === 0) {
      return <NoAnnotationsEmptyState />;
    }
    return <FilteredAnnotationsEmptyState />;
  }

  return (
    <div className="flex w-full flex-1 flex-col" onClick={navigateClickHandler}>
      {annotations.map((annotation) => (
        <AnnotationItem key={annotation.id} annotation={annotation} />
      ))}

      {hasNextPage && (
        <div className="flex w-full items-center justify-center">
          {isFetchingNextPage ? (
            <div className="space-x-2">
              <Spinner className="h-4 w-4" />
              <span>Fetching annotations...</span>
            </div>
          ) : (
            <button className="btn btn-small btn-depth" onClick={handleShowMore}>
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
